import React, { useEffect, useRef, useState } from 'react';
import { Scale, Award, Heart } from 'lucide-react';

const LAWYER_IMAGE = 'https://customer-assets.emergentagent.com/job_ec80005d-fba4-4969-be74-853cc224eb78/artifacts/aelazn5y_Season_Spanski%20%283%29.jpeg';

const credentials = [
  {
    icon: Scale,
    title: 'Expert Legal Guidance',
    description: 'Focused practice in Florida estate planning and real property law'
  },
  {
    icon: Award,
    title: 'Florida Licensed',
    description: 'Fully licensed and authorized to practice law throughout Florida'
  },
  {
    icon: Heart,
    title: 'Client-Centered Approach',
    description: 'Dedicated to understanding your unique needs and protecting your family'
  },
];

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="relative py-24 lg:py-32 bg-[#0a0a0a] overflow-hidden" ref={sectionRef}>
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 divider-elegant" />
      
      {/* Background decoration */}
      <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-[#c9a87c]/5 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div 
            className={`relative ${
              isVisible ? 'animate-slide-in-left' : 'opacity-0'
            }`}
          >
            <div className="relative">
              {/* Decorative frames */}
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#c9a87c]/20 rounded-lg" />
              <div className="absolute -bottom-4 -right-4 w-full h-full bg-gradient-to-br from-[#c9a87c]/10 to-transparent rounded-lg" />
              
              {/* Main image */}
              <div className="relative overflow-hidden rounded-lg">
                <img
                  src={LAWYER_IMAGE}
                  alt="Season A. Spanski, Florida Estate Planning Attorney"
                  className="w-full h-auto object-cover"
                />
                {/* Subtle overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 via-transparent to-transparent" />
              </div>

              {/* Name badge */}
              <div className="absolute bottom-6 left-6 right-6 glass rounded-lg p-4">
                <h3 className="font-serif text-xl text-[#faf9f7]">Season A. Spanski</h3>
                <p className="font-sans text-sm text-[#c9a87c]">Attorney at Law</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <span 
              className={`font-sans text-xs tracking-[0.3em] uppercase text-[#c9a87c] mb-4 block ${
                isVisible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
            >
              Meet Your Attorney
            </span>
            
            <h2 
              className={`font-serif text-3xl md:text-4xl lg:text-5xl text-[#faf9f7] mb-6 ${
                isVisible ? 'animate-fade-in-up delay-100' : 'opacity-0'
              }`}
            >
              Trusted Legal Counsel for <span className="text-gradient-gold">Florida Families</span>
            </h2>
            
            <p 
              className={`font-sans text-lg text-[#faf9f7]/70 mb-6 leading-relaxed ${
                isVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'
              }`}
            >
              At Spanski Law, PLLC, I understand that planning for your family's future is deeply personal. 
              A Lady Bird Deed isn't just a legal document — it's a way to ensure your loved ones are 
              protected and your wishes are honored.
            </p>
            
            <p 
              className={`font-sans text-[#faf9f7]/60 mb-8 leading-relaxed ${
                isVisible ? 'animate-fade-in-up delay-300' : 'opacity-0'
              }`}
            >
              I take the time to understand your unique situation, explain your options clearly, and 
              craft a Lady Bird Deed that's properly drafted under Florida law. Because when it comes 
              to protecting your home and your family's future, every detail matters.
            </p>

            {/* Credentials */}
            <div 
              className={`space-y-4 mb-8 ${
                isVisible ? 'animate-fade-in-up delay-400' : 'opacity-0'
              }`}
            >
              {credentials.map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a87c]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#c9a87c]" />
                  </div>
                  <div>
                    <h4 className="font-sans font-medium text-[#faf9f7] mb-1">{item.title}</h4>
                    <p className="font-sans text-sm text-[#faf9f7]/60">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <a
              href="#contact"
              className={`btn-elegant inline-flex items-center px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold tracking-wide rounded-sm hover:bg-[#d4b896] ${
                isVisible ? 'animate-fade-in-up delay-500' : 'opacity-0'
              }`}
            >
              Schedule Your Consultation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
